import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';

/**
 * The vocabulary for showing work in progress.
 *
 * A circular spinner is a poor fit for an agent: it implies a fixed, short wait, and an ingestion
 * run is neither — it can sit inside one model call for the better part of a minute and then move
 * through six tools in five seconds. These components say "alive, at a pace" instead, and they are
 * shared so a live run reads the same in a list row, a drawer header and a timeline.
 *
 * The motion itself lives in index.css as `cdr-*` utilities (and switches off wholesale under
 * `prefers-reduced-motion`); this file is composition and colour only.
 */

type Tone = 'accent' | 'ink' | 'good' | 'warn' | 'danger';

const CORE: Record<Tone, string> = {
  accent: 'bg-orange-500',
  ink: 'bg-ink-muted',
  good: 'bg-emerald-500',
  warn: 'bg-amber-500',
  danger: 'bg-red-500',
};

const HALO: Record<Tone, string> = {
  accent: 'bg-orange-500/35',
  ink: 'bg-ink-muted/30',
  good: 'bg-emerald-500/30',
  warn: 'bg-amber-500/30',
  danger: 'bg-red-500/30',
};

/**
 * A pulse with two haloes leaving it, half a cycle apart.
 *
 * The staggering is the whole trick: one halo reads as a blink, two offset read as something
 * breathing. Sized in pixels rather than by class so a row, a header and a rail can share one
 * component without three variants.
 */
export function BreathingDot({
  size = 8,
  tone = 'accent',
  className,
}: {
  size?: number;
  tone?: Tone;
  className?: string;
}) {
  return (
    <span
      className={cn('relative inline-flex flex-shrink-0 items-center justify-center', className)}
      style={{ width: size, height: size }}
      aria-hidden
    >
      <span className={cn('absolute inset-0 rounded-full cdr-halo', HALO[tone])} />
      <span className={cn('absolute inset-0 rounded-full cdr-halo-delayed', HALO[tone])} />
      <span className={cn('absolute inset-0 rounded-full cdr-breathe', CORE[tone])} />
    </span>
  );
}

/**
 * A dot with a label beside it, for a header that has to say what is happening.
 *
 * The label is not animated. Moving text is harder to read, and the point of the motion is to draw
 * the eye to something that then holds still long enough to be read.
 */
export function BreathingLabel({
  label,
  detail,
  tone = 'accent',
  className,
}: {
  label: string;
  detail?: string | null;
  tone?: Tone;
  className?: string;
}) {
  return (
    <span className={cn('inline-flex items-center gap-2 min-w-0', className)}>
      <BreathingDot size={9} tone={tone} />
      <span className="text-xs font-medium text-ink truncate">{label}</span>
      {detail && <span className="text-[11px] text-ink-faint truncate">{detail}</span>}
    </span>
  );
}

/**
 * A surface with a light sweeping across it — for the row that is currently active.
 *
 * `pointer-events-none` on the sweep and `isolate` on the wrapper because this sits *over* real
 * content: a highlight that swallowed clicks would make the active row the one row you cannot
 * interact with.
 */
export function SweepSurface({
  active = true,
  children,
  className,
}: {
  active?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('relative isolate overflow-hidden', className)}>
      {active && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 cdr-sweep bg-gradient-to-r from-transparent via-orange-500/10 to-transparent"
        />
      )}
      {children}
    </div>
  );
}

/**
 * Placeholder lines for content that has not arrived, with a sweep across them.
 *
 * Varying widths on purpose: three identical bars read as a loading graphic, three uneven ones read
 * as text about to appear.
 */
export function BreathingSkeleton({ lines = 3, className }: { lines?: number; className?: string }) {
  const widths = ['92%', '78%', '85%', '64%', '88%'];
  return (
    <div className={cn('space-y-2', className)} aria-hidden>
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className="relative isolate h-2.5 overflow-hidden rounded-full bg-surface-muted">
          <span
            className="absolute inset-y-0 left-0 rounded-full bg-border-subtle"
            style={{ width: widths[i % widths.length] }}
          />
          <span className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 cdr-sweep bg-gradient-to-r from-transparent via-white/50 to-transparent dark:via-white/10" />
        </div>
      ))}
    </div>
  );
}

/**
 * Text that is still being written: a highlight travels through the words, and a caret sits at the
 * end. Both stop the moment `streaming` goes false, so the transition to settled prose is the
 * absence of motion rather than a different component swapping in.
 */
export function StreamingText({
  text,
  streaming,
  className,
}: {
  text: string;
  streaming: boolean;
  className?: string;
}) {
  return (
    <span className={cn('whitespace-pre-wrap break-words', className)}>
      <span className={streaming ? 'cdr-streaming-text' : undefined}>{text}</span>
      {streaming && <span className="cdr-caret ml-0.5 inline-block h-3 w-[2px] translate-y-[1px] bg-orange-500" aria-hidden />}
    </span>
  );
}

/**
 * The rail down the left of a live timeline: a dashed gradient that drifts downward.
 *
 * Drawn as a repeating background rather than as elements so it costs one painted box however long
 * the timeline gets, and so the drift is one compositor animation rather than one per item.
 */
export function LiveRail({ live, className }: { live: boolean; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn('absolute top-0 bottom-0 w-px', live ? 'cdr-rail' : '', className)}
      style={
        live
          ? {
              backgroundImage:
                'repeating-linear-gradient(to bottom, var(--color-accent) 0 6px, transparent 6px 12px)',
              backgroundSize: '1px 24px',
              opacity: 0.55,
            }
          : { backgroundColor: 'var(--color-border)' }
      }
    />
  );
}
